import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";

import Select from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { getIn } from "formik";

import axios from "axios";

import UiSelect from "../ui/Select";
import "./select.scss";

const setupOptions = (options) => {
  if (
    Array.isArray(options) &&
    options[0] &&
    typeof options[0]?.id === "undefined" &&
    typeof options[0]?.options === "undefined"
  ) {
    return options.map((option) => ({ id: option, name: option }));
  }

  return options;
};

export const LegacySelect = ({
  className,
  clearable = false,
  creatable = false,
  readOnly = false,
  disabled = false,
  placeholder,
  field,
  form,
  defaultValue = null,
  isMulti = false,
  options: originalOptions,
  optionsUrl,
  optionsMethod = "get",
  onSelectionChange,
  prependStaticOptions: originalPrependStaticOptions = [],
  components,
  filterOption,
  hideSelectedOptions,
  maxMenuHeight,
  isSearchable,
  initiallyOpen,
  formatOptionLabel,
  styles,
}) => {
  const [loadedOptions, setLoadedOptions] = useState();
  const loadTimeoutRef = useRef();
  const pendingValueRef = useRef();
  const selectedValueRef = useRef(null);

  const options = useMemo(() => {
    return setupOptions(loadedOptions || originalOptions);
  }, [originalOptions, loadedOptions]);

  const prependStaticOptions = useMemo(
    () => setupOptions(originalPrependStaticOptions),
    [originalPrependStaticOptions]
  );

  const prependStaticOptionsRef = useRef(prependStaticOptions);
  useEffect(() => {
    prependStaticOptionsRef.current = prependStaticOptions;
  }, [prependStaticOptions]);

  const onChange = (option) => {
    if (!option && !clearable) return;

    const selection = isMulti ? option?.map((item) => item.id) : option?.id;

    // Prevent re-render loop: Formik's setFieldValue is async so field.value lags behind.
    // Use a ref to track the last value we submitted so rapid repeated calls are skipped.
    if (!isMulti && (selection == field?.value || selection === pendingValueRef.current)) return;

    pendingValueRef.current = selection;

    // Defer setFieldValue to prevent React error #185.
    // react-select can fire onChange during the render phase (e.g. when value prop changes
    // while options are loading). Calling setState directly during render causes the
    // "Too many re-renders" crash. setTimeout pushes the update to the next macrotask,
    // safely outside any active render cycle.
    setTimeout(() => {
      form?.setFieldValue(field?.name, selection);
      onSelectionChange && onSelectionChange(selection);
    }, 0);
  };

  const defaltFormatOptionLabel = ({ name, description }) => {
    if (!description || !name) {
      return name;
    }

    if (description.substr(0, name.length) === name) {
      description = description.substr(name.length);

      while (description.length && description[0] === ",") {
        description = description.substr(1);
      }
    }

    description = " " + description;

    return (
      <>
        <span>{name}</span>

        {description && <small>{description}</small>}
      </>
    );
  };

  const selectedValue = useMemo(() => {
    let found;

    if (options) {
      if (isMulti) {
        found = options.filter(
          (option) => field?.value?.indexOf(option.id) >= 0
        );
      } else if (options[0]?.options) {
        found = options.reduce((acc, option) => {
          if (acc) return acc;
          return option.options.find(
            (subOption) => subOption.id == field?.value
          );
        }, null) ?? null;
      } else {
        found = options.find((option) => option.id === field?.value) ?? null;
      }
    } else {
      found = isMulti ? [] : null;
    }

    // Return the cached reference when the selected id hasn't changed.
    // This keeps the value prop reference stable so react-select's internal
    // useEffect doesn't fire onChange on every render.
    if (!isMulti && found?.id === selectedValueRef.current?.id) {
      return selectedValueRef.current;
    }

    selectedValueRef.current = found;
    return found;
  }, [options, field?.value, isMulti]);

  const loadOptions = useCallback((inputValue, callback) => {
    const makeRequest = () => {
      axios[optionsMethod](optionsUrl, {
        params: {
          filter: inputValue,
        },
      }).then((response) => {
        // A 5xx / dropped connection resolves undefined (global interceptor).
        // Always answer react-select, or the dropdown spins forever.
        if (!Array.isArray(response?.data)) {
          callback([...prependStaticOptionsRef.current]);
          return;
        }
        const newOptions = [...prependStaticOptionsRef.current, ...response.data];
        setLoadedOptions(newOptions);
        callback(newOptions);
      }).catch(() => {
        callback([...prependStaticOptionsRef.current]);
      });
    };

    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }

    loadTimeoutRef.current = setTimeout(makeRequest, 400);
  }, [optionsUrl, optionsMethod]);

  const selectProps = {
    className: className || "select-container",
    classNamePrefix: "select",
    name: field?.name,
    value: selectedValue ?? defaultValue,
    onChange,
    isDisabled: disabled,
    readOnly: readOnly,
    placeholder,
    formatOptionLabel: formatOptionLabel || defaltFormatOptionLabel,
    options,
    isMulti,
    defaultOptions: true,
    getOptionLabel: (option) => option.name,
    getOptionValue: (option) => option.id || option,
    menuPortalTarget: document.body,
    styles: {
      menuPortal: (base) => ({ ...base, zIndex: 9999999999999 }),
      control: (base, s) => ({
        ...base,
        ...(s.isDisabled && {
          backgroundColor: "#f3f4f6",
          borderColor: "#e5e7eb",
          color: "#9ca3af",
          opacity: 0.6,
          cursor: "not-allowed",
        }),
      }),
      valueContainer: (base) => ({
        ...base,
        padding: "2px 4px",
      }),
      singleValue: (base) => ({
        ...base,
        marginLeft: 0,
        // Inherit color from the surrounding scope (.confirm-request-desktop
        // forces dark, .find-product-product forces white, etc.). Emotion's
        // default explicit color used to win every CSS cascade because of how
        // emotion injects high-priority class rules — making it `inherit`
        // hands control back to the page CSS.
        color: "inherit",
      }),
      placeholder: (base) => ({
        ...base,
        marginLeft: 0,
        color: "inherit",
        opacity: 0.6,
      }),
      ...(styles || {}),
    },
    components,
    filterOption,
    hideSelectedOptions,
    maxMenuHeight: maxMenuHeight || 170,
    isSearchable,
    autoFocus: initiallyOpen,
    openMenuOnFocus: initiallyOpen,
  };

  let SelectComponent = Select;
  let componentKey = null;

  if (optionsUrl) {
    SelectComponent = AsyncSelect;
    componentKey = optionsUrl; // Store key separately, don't add to selectProps
    selectProps["loadOptions"] = loadOptions;
  }

  if (creatable) {
    SelectComponent = CreatableSelect;

    selectProps["getNewOptionData"] = (inputValue, optionLabel) => {
      return {
        id: optionLabel,
        name: optionLabel,
      };
    };

    selectProps["formatCreateLabel"] = (input) => "Confirm: " + input;
    selectProps["onCreateOption"] = (inputValue) => {
      setLoadedOptions([
        ...options,
        {
          id: inputValue,
          name: inputValue,
          __isNew__: true,
        },
      ]);
    };
  }

  // Pass key directly, not through spread
  return componentKey ? (
    <SelectComponent key={componentKey} {...selectProps} />
  ) : (
    <SelectComponent {...selectProps} />
  );
};



// ── V2 (Fontaine ui/Select) ─────────────────────────────────────────────────
// Every FieldGroup type="select" and every direct `Select` import renders this:
// the same props contract as LegacySelect above ({id, name} / primitive / grouped
// options, Formik `field`/`form`, onSelectionChange(id), optionsUrl, creatable,
// isMulti), drawn by components/ui/Select. Pass `legacy` to stay on react-select —
// only for call sites that need a react-select-only feature (custom `components`,
// `initiallyOpen`); see the opt-outs listed where they are used.

// ui/Select treats null / "" as "no value", but a few lists carry a real option
// with that id ("All", "All Models"), so map it to a sentinel and back.
const EMPTY = "__rc-select-empty__";
const toKey = (id) => (id === null || id === undefined || id === "" ? EMPTY : id);

// The secondary text LegacySelect's default formatter showed: `description`
// minus the leading repeat of `name`.
const descriptionHint = ({ name, description }) => {
  if (!description || typeof name !== "string") return undefined;
  const rest = description.startsWith(name) ? description.slice(name.length).replace(/^,+/, "") : description;
  return rest.trim() || undefined;
};

const toUiOptions = (options, formatOptionLabel) =>
  (setupOptions(options) || []).flatMap((o) =>
    o?.options
      ? setupOptions(o.options).map((s) => toUiOption(s, formatOptionLabel, o.label ?? o.name))
      : [toUiOption(o, formatOptionLabel)]
  );

const toUiOption = (o, formatOptionLabel, group) => ({
  value: toKey(o.id),
  label: formatOptionLabel ? formatOptionLabel(o, { context: "menu" }) : o.name,
  hint: formatOptionLabel ? undefined : descriptionHint(o),
  disabled: o.isDisabled || o.disabled || undefined,
  group,
  raw: o,
});

const mergeById = (prev, next) => {
  const seen = new Set((prev || []).map((o) => o.id));
  return [...(prev || []), ...next.filter((o) => !seen.has(o.id))];
};

const V2Select = ({
  className,
  clearable = false,
  creatable = false,
  disabled = false,
  placeholder,
  field,
  form,
  id,
  defaultValue = null,
  isMulti = false,
  options: originalOptions,
  optionsUrl,
  optionsMethod = "get",
  onSelectionChange,
  prependStaticOptions = [],
  isSearchable,
  formatOptionLabel,
}) => {
  const [loaded, setLoaded] = useState(null);
  const [created, setCreated] = useState([]);

  // Inline literals at most call sites — read through a ref so the async
  // loader keeps one identity (ui/Select re-queries whenever it changes).
  const prependRef = useRef(prependStaticOptions);
  prependRef.current = prependStaticOptions;

  const fetchOptions = useCallback(
    async (query) => {
      let rows = [];
      try {
        // A 5xx resolves undefined through the global interceptor — treat it as empty.
        const response = await axios[optionsMethod](optionsUrl, { params: { filter: query } });
        if (Array.isArray(response?.data)) rows = response.data;
      } catch {
        // empty list; the static prepends still show
      }
      const list = [...(setupOptions(prependRef.current) || []), ...(setupOptions(rows) || [])];
      setLoaded((prev) => mergeById(prev, list));
      return list;
    },
    [optionsUrl, optionsMethod]
  );

  // LegacySelect loaded the first page on mount (defaultOptions), so a stored
  // id shows its label before the menu is ever opened.
  useEffect(() => {
    if (!optionsUrl) return;
    setLoaded(null);
    fetchOptions("");
  }, [optionsUrl, fetchOptions]);

  const loadOptions = useMemo(
    () => (optionsUrl ? async (query) => toUiOptions(await fetchOptions(query), formatOptionLabel) : undefined),
    [optionsUrl, fetchOptions, formatOptionLabel]
  );

  const uiOptions = useMemo(() => {
    const base = optionsUrl ? loaded || setupOptions(prependStaticOptions) || [] : originalOptions;
    return toUiOptions([...(base || []), ...created], formatOptionLabel);
    // prependStaticOptions only matters before the first load; read it then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsUrl, loaded, originalOptions, created, formatOptionLabel]);

  // Only show a value that is a real option, as LegacySelect did — otherwise
  // ui/Select would print the raw id. Grouped lists matched loosely (==) there.
  const grouped = !!setupOptions(originalOptions)?.[0]?.options;
  const resolve = (v) => {
    const key = toKey(v);
    const hit = uiOptions.find((o) => o.value === key) || (grouped ? uiOptions.find((o) => o.value == key) : undefined);
    return hit?.value;
  };
  // `defaultValue` arrives as an option, an [option] or a bare id across callers.
  const fallback = Array.isArray(defaultValue) ? defaultValue[0] : defaultValue;
  const defaultIds = fallback == null ? [] : [typeof fallback === "object" ? fallback.id : fallback];

  const stored = field?.value;
  let value;
  if (isMulti) {
    value = (Array.isArray(stored) ? stored : []).map(resolve).filter((v) => v !== undefined);
  } else {
    value = resolve(stored);
    if (value === undefined && defaultIds.length) value = resolve(defaultIds[0]);
    if (value === undefined) value = null;
  }

  const idOf = (opt) => (opt?.raw ? opt.raw.id : opt?.value === EMPTY ? null : opt?.value);

  const onChange = (next, opt) => {
    if (opt == null && !clearable) return;
    const selection = isMulti ? (opt || []).map(idOf) : idOf(opt);
    // Re-picking the current value is a no-op, as before.
    if (!isMulti && selection == stored) return;
    form?.setFieldValue(field?.name, selection);
    onSelectionChange && onSelectionChange(selection);
  };

  const onCreate = creatable
    ? (label) => {
        const option = { id: label, name: label };
        setCreated((prev) => [...prev, option]);
        return { value: label, label, raw: option };
      }
    : undefined;

  const name = field?.name;
  const invalid = !!(name && getIn(form?.touched, name) && getIn(form?.errors, name));

  return (
    <UiSelect
      key={optionsUrl || undefined}
      id={id}
      className={["rc-select", className].filter(Boolean).join(" ")}
      fullWidth
      options={uiOptions}
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      onCreate={onCreate}
      multi={isMulti}
      clearable={clearable}
      // Creating needs the text box; otherwise ui/Select decides (on above 8 options).
      searchable={creatable || isSearchable ? true : isSearchable === false ? false : undefined}
      disabled={disabled}
      invalid={invalid}
      placeholder={placeholder ?? "Select…"}
      // A FieldGroup <label htmlFor={id}> names the trigger; aria-label would override it.
      label={typeof placeholder === "string" && placeholder ? placeholder : id ? undefined : name}
    />
  );
};

const CustomSelect = ({ legacy = false, ...props }) =>
  legacy ? <LegacySelect {...props} /> : <V2Select {...props} />;

export default CustomSelect;
